/**
 * What a node IS, from where it lives.
 *
 * The graph coloured every node the same accent, with an optional user-defined colour group on
 * top. That is Obsidian's model and it is right for a vault of prose, but MO OS's vault is TYPED:
 * `mo/people`, `mo/orgs`, `mo/commitments` and five more folders, each holding one kind of thing.
 * A graph that draws 6,361 identical dots is throwing away the one fact that would make it
 * readable.
 *
 * THE VOCABULARY IS SHARED, DELIBERATELY. These are the same eight kinds and the same eight hue
 * tokens the core nucleus orbits, so purple means a person on both surfaces. Two colour schemes
 * for one taxonomy would be worse than one arbitrary scheme.
 *
 * Folder prefix rather than frontmatter: the path is already on every graph node, and reading
 * frontmatter would mean the graph worker parsing 6,361 files to draw a dot.
 */

export type MoKind =
  | 'person'
  | 'org'
  | 'topic'
  | 'commitment'
  | 'meeting'
  | 'thread'
  | 'decision'
  | 'objective'
  /** Something he published. The only kind here that is his output rather than his input. */
  | 'post'
  /** A note that is not part of the MO layer at all: daily notes, drafts, his own writing. */
  | 'note'
  /** Images and files. */
  | 'attachment'
  /** An unresolved link target: named by something, but nothing there yet. */
  | 'phantom';

/** Folder prefix to kind. Mirrors FOLDERS in apps/server/src/mo/nodes.ts. */
const BY_FOLDER: readonly [string, MoKind][] = [
  ['mo/people/', 'person'],
  ['mo/orgs/', 'org'],
  ['mo/topics/', 'topic'],
  ['mo/commitments/', 'commitment'],
  ['mo/meetings/', 'meeting'],
  ['mo/threads/', 'thread'],
  ['mo/decisions/', 'decision'],
  ['mo/objectives/', 'objective'],
  ['mo/posts/', 'post'],
];

/** The token each kind takes its colour from, and the label a legend shows. */
export const KIND_STYLE: Record<MoKind, { token: string; label: string; fallback: string }> = {
  person: { token: '--hue-know', label: 'People', fallback: '#c4a2ff' },
  org: { token: '--hue-action', label: 'Organisations', fallback: '#4d94ff' },
  topic: { token: '--hue-intent', label: 'Topics', fallback: '#3ddc97' },
  commitment: { token: '--hue-waiting', label: 'Commitments', fallback: '#fbbf24' },
  meeting: { token: '--hue-time', label: 'Meetings', fallback: '#5eead4' },
  thread: { token: '--hue-ask', label: 'Correspondence', fallback: '#ff8fab' },
  decision: { token: '--danger', label: 'Decisions', fallback: '#ff6b81' },
  objective: { token: '--hue-health', label: 'Objectives', fallback: '#7dd3fc' },
  post: { token: '--hue-voice', label: 'Published', fallback: '#f0883e' },
  note: { token: '--text-muted', label: 'Other notes', fallback: '#bccbe4' },
  attachment: { token: '--text-faint', label: 'Attachments', fallback: '#9aabc9' },
  phantom: { token: '--border-strong', label: 'Not written yet', fallback: '#6b80ad' },
};

/**
 * Classify a node.
 *
 * `unresolved` wins over the path, because a phantom's "path" is a link target someone typed and
 * may well look like a real folder. Attachments are decided by extension rather than folder,
 * since Obsidian puts them wherever the vault owner likes.
 */
export function kindOf(node: { path: string; unresolved?: boolean }): MoKind {
  if (node.unresolved === true) return 'phantom';
  const p = node.path.toLowerCase();
  if (!p.endsWith('.md')) return 'attachment';
  for (const [prefix, kind] of BY_FOLDER) if (p.startsWith(prefix)) return kind;
  return 'note';
}

/** Resolve every kind's colour once from the live theme, so no draw call touches the DOM. */
export function readKindColors(): Record<MoKind, string> {
  const cs = getComputedStyle(document.documentElement);
  const out = {} as Record<MoKind, string>;
  for (const key of Object.keys(KIND_STYLE) as MoKind[]) {
    const s = KIND_STYLE[key];
    out[key] = cs.getPropertyValue(s.token).trim() || s.fallback;
  }
  return out;
}
